import React from 'react';
import mfaEnableImgSrc from '../images/two-step-verification/mfa-enable.png';
import { gridSize } from '@atlaskit/theme';
import styled from '@emotion/styled';
import WhiteboxPage from '../components/WhiteboxPage';
import TallButton from '../components/TallButton';
import ButtonContainer from '../components/ButtonContainer';
import ButtonLink from '../components/ButtonLink';
import { FormattedMessage } from 'react-intl';
import {
  useButtonClickedEvent,
  useOperationalEvent,
  usePageViewedEvent,
} from '../utilities/analytics/analytics-web-client';
import messages from './MfaPromotePage.messages';
import { handleMfaPromoteUserSelection } from '../actions/mfa-promote-actions';
import { useForceRedirect } from '../utilities/hooks';
import { usePageStateLoader, fetchPageState } from '../utilities/hooks/useGlobalPageState';
import { useAppDispatch, useAppSelector } from '../stores/redux.store';
import { useLocation } from 'react-router-dom';
import { AidFrontendFeatureGate } from '../providers/FeatureGates';
import {
  checkFeatureGate,
  checkFeatureGateOnceInitialized,
} from '../utilities/feature-flags/feature-gate';
import Heading from '@atlaskit/heading';
import { Box } from '@atlaskit/primitives';

const MfaPromoteContainer = styled.div`
  margin-top: ${gridSize()}px;
`;

const MfaImg = styled.img`
  height: ${gridSize() * 14}px;
  display: block;
  margin: 0 auto;
  padding: ${gridSize()}px 0;
`;

const ContentBody = styled.p`
  margin: ${gridSize()}px 0;
`;

const pageId = 'mfaPromotePage';

export async function securityScreenLoader({ request }: { request: Request }) {
  if (!(await checkFeatureGateOnceInitialized(AidFrontendFeatureGate.USE_STATE_DATA_LOADERS))) {
    return null;
  }

  return await fetchPageState('/rest/login/security-screen/state', request, {
    capabilities: ['capability-group--login', 'capability-name--mfa-promote'],
  });
}

type MfaPromotePageProps = {
  trackMfaLeanMoreLinkClicked: () => void;
  onMfaContinueButtonClicked: () => void;
  onDismissButtonClicked: () => void;
};

const BaseMfaPromotePage = ({
  trackMfaLeanMoreLinkClicked,
  onMfaContinueButtonClicked,
  onDismissButtonClicked,
}: MfaPromotePageProps) => {
  return (
    <WhiteboxPage
      pageId="mfaPromotePage"
      header={
        <Heading as="h1" size="small">
          <FormattedMessage {...messages.promoteMfaHeader} />
        </Heading>
      }
      whiteboxFooter={
        <ButtonLink
          id="mfa-promote-learn-more"
          onClick={trackMfaLeanMoreLinkClicked}
          href="https://support.atlassian.com/atlassian-account/docs/manage-two-step-verification-for-your-atlassian-account/"
          target="_blank"
          rel="noopener noreferrer">
          <FormattedMessage {...messages.promoteMfaLearnMore} />
        </ButtonLink>
      }>
      <MfaPromoteContainer>
        <MfaImg src={mfaEnableImgSrc} />
        <ContentBody>
          <FormattedMessage {...messages.promoteMfaMessageBody} />
        </ContentBody>
        <ButtonContainer key="mfa.promote.continue.container">
          <TallButton
            appearance="primary"
            id="mfa-promote-continue"
            type="submit"
            isFullWidth
            onClick={onMfaContinueButtonClicked}>
            <FormattedMessage {...messages.promoteMfaContinue} />
          </TallButton>
        </ButtonContainer>
        <ButtonContainer key="mfa.promote.dismiss.container">
          <TallButton
            appearance="default"
            id="mfa-promote-dismiss"
            testId="mfa-promote-dismiss-idf-testid"
            type="submit"
            isFullWidth
            onClick={onDismissButtonClicked}>
            <FormattedMessage {...messages.promoteMfaDismiss} />
          </TallButton>
        </ButtonContainer>
      </MfaPromoteContainer>
    </WhiteboxPage>
  );
};

type MfaPasskeysPromotePageProps = MfaPromotePageProps & {
  trackPasskeysLeanMoreLinkClicked: () => void;
  onPasskeysContinueButtonClicked: () => void;
};

const NewMfaPasskeysPromotePage = ({
  trackMfaLeanMoreLinkClicked,
  onMfaContinueButtonClicked,
  trackPasskeysLeanMoreLinkClicked,
  onPasskeysContinueButtonClicked,
  onDismissButtonClicked,
}: MfaPasskeysPromotePageProps) => {
  return (
    <WhiteboxPage
      pageId="mfaPromotePage"
      header={
        <Heading as="h1" size="small">
          <FormattedMessage {...messages.promoteMfaPasskeysHeader} />
        </Heading>
      }
      whiteboxFooter={
        <>
          <ButtonLink
            id="mfa-promote-learn-more"
            onClick={trackMfaLeanMoreLinkClicked}
            href="https://support.atlassian.com/atlassian-account/docs/manage-two-step-verification-for-your-atlassian-account/"
            target="_blank"
            rel="noopener noreferrer"
            shouldFitContainer>
            <FormattedMessage {...messages.promoteMfaLearnMore} />
          </ButtonLink>
          {/* TODO: KIRBY-10063 - passkeys should only be shown if user doesn't have one */}
          <ButtonLink
            id="passkeys-promote-learn-more"
            onClick={trackPasskeysLeanMoreLinkClicked}
            href="https://support.atlassian.com/atlassian-account/docs/access-your-atlassian-account-with-a-passkey/"
            target="_blank"
            rel="noopener noreferrer"
            shouldFitContainer>
            <FormattedMessage {...messages.promotePasskeysLearnMore} />
          </ButtonLink>
        </>
      }>
      <MfaPromoteContainer>
        <MfaImg src={mfaEnableImgSrc} />
        <ContentBody>
          <FormattedMessage {...messages.promoteMfaMessageBody} />
        </ContentBody>
        <ButtonContainer key="mfa.promote.continue.container">
          <TallButton
            appearance="primary"
            id="mfa-promote-continue"
            type="submit"
            isFullWidth
            onClick={onMfaContinueButtonClicked}>
            <FormattedMessage {...messages.promoteMfaContinue} />
          </TallButton>
        </ButtonContainer>
        <ButtonContainer key="mfa.promote.dismiss.container">
          <TallButton
            appearance="default"
            id="mfa-promote-dismiss"
            testId="mfa-promote-dismiss-idf-testid"
            type="submit"
            isFullWidth
            onClick={onDismissButtonClicked}>
            <FormattedMessage {...messages.promoteMfaDismiss} />
          </TallButton>
        </ButtonContainer>

        {/* TODO: KIRBY-10063 - passkeys should only be shown if user doesn't have one */}
        <Box paddingBlockStart="space.300">
          <ContentBody>
            <FormattedMessage {...messages.promotePasskeysMessageBody} />
          </ContentBody>
          <ButtonContainer key="passkeys.promote.continue.container">
            <TallButton
              appearance="default"
              id="passkeys-promote-continue"
              type="submit"
              isFullWidth
              onClick={onPasskeysContinueButtonClicked}>
              <FormattedMessage {...messages.promotePasskeysContinue} />
            </TallButton>
          </ButtonContainer>
        </Box>
      </MfaPromoteContainer>
    </WhiteboxPage>
  );
};

export const MfaPromotePage = () => {
  const dispatch = useAppDispatch();
  const redirectTo = useAppSelector(state => state.mfaPromote.redirectTo);

  useForceRedirect({ redirectTo });
  usePageViewedEvent(pageId);
  usePageStateLoader();
  useOperationalEvent({
    page: pageId,
    action: `loadMfaPromotePageSuccessful`,
    subject: 'sli',
  });

  const { search } = useLocation();

  const trackMfaContinueButtonClicked = useButtonClickedEvent(pageId, 'MfaPromoteContinue');
  const trackMfaLeanMoreLinkClicked = useButtonClickedEvent(pageId, 'MfaPromoteLearnMore');
  // TODO: KIRBY-10058 - add analytics tracking for passkeys
  const trackPasskeysLeanMoreLinkClicked = useButtonClickedEvent(
    pageId,
    'PasskeysPromoteLearnMore'
  );
  const trackDismissButtonClicked = useButtonClickedEvent(pageId, 'MfaPromoteDismiss');

  const onMfaContinueButtonClicked = () => {
    trackMfaContinueButtonClicked();
    dispatch(handleMfaPromoteUserSelection('edit_mfa', search));
  };

  const onPasskeysContinueButtonClicked = () => {
    // TODO: KIRBY-10064 - id-auth returns option to redirect to MPUI passkeys page
  };

  const onDismissButtonClicked = () => {
    trackDismissButtonClicked();
    dispatch(handleMfaPromoteUserSelection('dismiss', search));
  };

  const shouldRenderGeneralSecurityScreen = checkFeatureGate(
    AidFrontendFeatureGate.RENDER_GENERAL_SECURITY_SCREEN
  );

  if (shouldRenderGeneralSecurityScreen) {
    return (
      <NewMfaPasskeysPromotePage
        trackMfaLeanMoreLinkClicked={trackMfaLeanMoreLinkClicked}
        onMfaContinueButtonClicked={onMfaContinueButtonClicked}
        trackPasskeysLeanMoreLinkClicked={trackPasskeysLeanMoreLinkClicked}
        onPasskeysContinueButtonClicked={onPasskeysContinueButtonClicked}
        onDismissButtonClicked={onDismissButtonClicked}
      />
    );
  }

  return (
    <BaseMfaPromotePage
      trackMfaLeanMoreLinkClicked={trackMfaLeanMoreLinkClicked}
      onMfaContinueButtonClicked={onMfaContinueButtonClicked}
      onDismissButtonClicked={onDismissButtonClicked}
    />
  );
};

export default MfaPromotePage;
